const NS = 'MarketEmiChartBlock';
const CLS = 'marketEmiChartBlock';

import { ChartBlock } from '../../chartBlock/js/chartBlock';

let moment = require('moment');
let d3 = require('d3');

export class MarketEmiChartBlock extends ChartBlock {
	constructor(element) {
		super(element, CLS);

		this.api = this.$element.attr('data-api');

		this.margin = {
			top: 10,
			right: 10,
			bottom: 50,
			left: 60
		};

		this.heightRatio = 0.55;

		this.dotRadius = 3;
		this.dotFocusRadius = 3;

		this.urlParams = {};

		this.$filtersSelect = this.$element.find(`.${CLS}-filters-select`);

		if(this.$filtersSelect && this.$filtersSelect.length > 0) {
			this.setFilterValueEvent();
			this.initFilterChangeEvent();
		}

		this.getChartDataFromApi();
	}

	setFilterValueEvent() {
		this.$filtersSelect.each((index, element) => {
			let $element = $(element);
			let filterKey = $element.data('filter-key');

			if(!(_.isNil(this.urlParams[filterKey]) && $element.val() === '')) {
				if(_.isNil($element.val()) || $element.val() === '') {
					delete this.urlParams[filterKey];
				}
				else {
					if(_.isArray($element.val()) && $element.val().length > 0) {
						this.urlParams[filterKey] = $element.val().join(',');
					}
					else {
						this.urlParams[filterKey] = $element.val();
					}
				}
			}
		});
	}

	initFilterChangeEvent() {
		this.$filtersSelect.on('change', (event) => {
			let $element = $(event.currentTarget);
			let filterKey = $element.data('filter-key');

			if(!(_.isNil(this.urlParams[filterKey]) && $element.val() === '')) {
				// Ensure that the filterKey does not match the current select value
				if(this.urlParams[filterKey] !== $element.val()) {
					if(_.isNil($element.val()) || $element.val() === '') {
						delete this.urlParams[filterKey];
					}
					else {
						if(_.isArray($element.val())) {
							this.urlParams[filterKey] = $element.val().join(',');
						}
						else {
							this.urlParams[filterKey] = $element.val();
						}
					}

					this.getChartDataFromApi(true);
				}
			}
		});
	}

	// Retrieves the data from the API
	getChartDataFromApi() {
		let url = this.api;

		if(!_.isEmpty(this.urlParams)) {
			url += `?${$.param(this.urlParams)}`;
		}

		if(this.api) {
			$.ajax({
				type: 'GET',
				url: url,
				dataType: 'json',
				success: (response) => {
					if(response && response.length > 0) {
						this.$element.trigger(`${CLS}.update`, {
							data: response
						});
					}
					else {
						this.$element.trigger(`${CLS}.update`, {
							data: null,
							isEmpty: true
						});
					}
				},
				error: () => {
					this.$element.trigger(`${CLS}.update`, {
						data: null
					});
				}
			});
		}
		else {
			console.error(`${NS}::Error`, 'API is not set.');
			this.$element.trigger(`${CLS}.update`, {
				data: null
			});
		}
	}

	// Have placeholder data that generates a generic x-y axes.
	setEmptyData() {
		// Placeholder data used, when there are no actual values…
		let placeholderData = [
			{
				key: 'AUD',
				label: 'AUD',
				values: []
			}
		];
		let initialPlaceholderDataValue = 1200;
		let initialPlaceholderIncrementValue = 125;

		for(let i = 0; i < 6; i++) {
			placeholderData[0].values.push({
				value: initialPlaceholderDataValue + (i * initialPlaceholderIncrementValue),
				date: moment().month(i).day(1)
			});
		}

		this.emptyData = placeholderData;
	}

	drawChart() {
		this.clearSvg();
		this.setChartDimensions();

		if(this.$tooltip && this.$tooltip.length > 0) {
			this.$tooltip.remove(); // Removes the tooltip
		}

		this.drawSvg();
		this.drawTooltip();

		this.defineXAxesScales();
		this.defineYAxesScales();

		this.drawXAxes();
		this.drawYAxes();

		if(!this.isDataEmpty) {
			this.drawAreaAesthetic();
			this.drawPaths();
			this.drawPoints();
		}
	}

	drawTooltip() {
		this.tooltip = this.getChartNode()
			.append('div')
			.attr('class', `${CLS}-chart-tooltip`);

		this.$tooltip = this.$chart.find(`.${CLS}-chart-tooltip`);
	}

	getXAxisBottomData() {
		let data = [];
		_.forEach(this.data, (datum) => {
			_.forEach(datum.values, (value) => {
				if(!_.isNil(value.date)) {
					data.push(value.date);
				}
			});
		});

		return data;
	}

	getYAxisLeftData() {
		let data = [];

		_.forEach(this.data, (datum) => {
			_.forEach(datum.values, (value) => {
				if(!_.isNil(value.value)) {
					data.push(value.value);
				}
			});
		});

		return data;
	}

	defineXAxesScales() {
		let data = this.getXAxisBottomData();
		this.xMinTickValue = data[0];
		this.xMaxTickValue = data[data.length - 1];

		if(this.xMinTickValue === this.xMaxTickValue) {
			this.xMaxTickValue = moment(this.xMinTickValue, 'DD/MM/YYYY').add(1, 'years').subtract(2, 'months').format('DD/MM/YYYY');
			data.push(this.xMaxTickValue);
		}

		this.xAxisBottom = d3.scaleTime()
			.range([0, this.width])
			.domain(d3.extent(data, (datum) => {
				return this.getMomentDate(datum).toDate();
			}));

		let xMinTickDate = moment(this.xMinTickValue, 'DD/MM/YYYY');
		let xMaxTickDate = moment(this.xMaxTickValue, 'DD/MM/YYYY');
		let duration = moment.duration(xMaxTickDate.diff(xMinTickDate));

		// Don’t round the dates when months < 3
		if(!(duration.asMonths() <= 3)) {
			this.xAxisBottom = this.xAxisBottom.nice();
		}
	}

	defineYAxesScales() {
		let data = this.getYAxisLeftData();
		let yMinTickValue = _.minBy(data, (datum) => {
			return datum;
		});

		let yMaxTickValue = _.maxBy(data, (datum) => {
			return datum;
		});

		if(yMinTickValue !== yMaxTickValue) {
			let range = yMaxTickValue - yMinTickValue;
			this.yTickValue = this.getLinearTick(range);

			this.yMaxTickValue = Math.ceil((yMaxTickValue) / this.yTickValue) * this.yTickValue;
			this.yMinTickValue = Math.floor((yMinTickValue / this.yTickValue)) * this.yTickValue;
		}
		else {
			this.yMaxTickValue = Math.ceil(yMaxTickValue);
			this.yMinTickValue = Math.floor(yMinTickValue);
			this.yTickValue = _.floor(this.yMaxTickValue, (((this.yMaxTickValue + '').toString().length) * -1) + 1); // Rounds to the negative {x} sf.; // Rounds to the negative {x} sf.
			if(this.yTickValue !== 0) {
				this.yMaxTickValue *= 2.05;
				this.yMinTickValue = 0;
			}
			else {
				this.yMinTickValue = -1;
				this.yTickValue = 1;
				this.yMaxTickValue = 1.05;
			}
		}

		this.yAxisLeft = d3.scaleLinear()
			.range([this.height, 0])
			.domain([
				this.yMinTickValue,
				this.yMaxTickValue
			]).nice();
	}

	drawXAxes() {
		let tickOffset = 10;

		let xMinTickDate = moment(this.xMinTickValue, 'DD/MM/YYYY');
		let xMaxTickDate = moment(this.xMaxTickValue, 'DD/MM/YYYY');
		let duration = moment.duration(xMaxTickDate.diff(xMinTickDate));

		// Render x-axes text values
		(() => {
			let outerIndex = 0;
			this.svg.append('g')
				.attr('class', 'x-axis')
				.attr('transform', `translate(0,${this.height + tickOffset})`)
				.call(d3.axisBottom(this.xAxisBottom).ticks(d3.timeDay.every(1)).tickFormat((value, index) => {
					if(duration.asMonths() <= 12) {
						if(duration.asMonths() <= 3) {
							// Return weekly date format if months <= 3
							if(index % 7 === 0) {
								return moment(value).format('DD/MM');
							}
						}
						else {
							// Return every first day of the month
							if(moment(value).date() === 1) {
								return moment(value).format('MMM YY');
							}
						}
					}
					else {
						// Return every first day of the month
						if(moment(value).date() === 1) {
							// Return {x} monthly value based on {x} yearly value
							if(outerIndex % (Math.floor(duration.asYears()) * 2) === 0) {
								outerIndex++;
								return moment(value).format('MMM YY');
							}
							outerIndex++;
						}
					}
					return '';
				}));
		})();

		// Render lines
		(() => {
			let outerIndex = 0;
			this.getChartNode().select('.x-axis').selectAll('line').attr('y2', (value, index, nodes) => {
				if(duration.asMonths() <= 12) {
					if(duration.asMonths() <= 3) {
						// Return weekly tick when if months <= 3
						if(index % 7 === 0) {
							return -(this.height + tickOffset);
						}
					}
					else {
						// Return monthly tick value
						if(moment(value).date() === 1) {
							return -(this.height + tickOffset);
						}
					}
				}
				else {
					if(moment(value).date() === 1) {
						// Return {x} monthly tick based on {x} yearly value
						if(outerIndex % (Math.floor(duration.asYears())) === 0) {
							return -(this.height + tickOffset);
						}
						outerIndex++;
					}
				}
				return 0;
			});
		})();

		// Conceal x-axes text values for mobile devices
		(() => {
			let ticks = this.getChartNode().select('.x-axis').selectAll('text');
			let outerIndex = 0;
			ticks.each((value, index, nodes) => {
				nodes[index].style.visibility = 'hidden';
				if(duration.asMonths() <= 12) {
					if(duration.asMonths() <= 3) {
						if(duration.asMonths() <= 1) {
							if(index % 7 === 0) {
								nodes[index].style.visibility = 'visible';
							}
						}
						else {
							// Return forntlightly on mobile for excess of one month
							if(this.mediaQuery.breakpointMaxWidth(this.mediaQuery.BREAKPOINTS.SM)) {
								if(index % 14 === 0) {
									nodes[index].style.visibility = 'visible';
								}
							}
							else {
								if(index % 7 === 0) {
									nodes[index].style.visibility = 'visible';
								}
							}
						}
					}
					else {
						if(moment(value).date() === 1) {
							if(duration.asMonths() > 8) {
								// Returns every other month on mobile when months > 8
								if(this.mediaQuery.breakpointMaxWidth(this.mediaQuery.BREAKPOINTS.SM)) {
									if(outerIndex % 3 === 0) {
										nodes[index].style.visibility = 'visible';
									}
									outerIndex++;
								}
								else {
									if(outerIndex % 2 === 0) {
										nodes[index].style.visibility = 'visible';
									}
									outerIndex++;
								}
							}
							else if(duration.asMonths() > 4) {
								if(this.mediaQuery.breakpointMaxWidth(this.mediaQuery.BREAKPOINTS.SM)) {
									if(outerIndex % 2 === 0) {
										nodes[index].style.visibility = 'visible';
									}
									outerIndex++;
								}
								else {
									nodes[index].style.visibility = 'visible';
								}
							}
							else {
								nodes[index].style.visibility = 'visible';
							}
						}
					}
				}
				else {
					if(moment(value).date() === 1) {
						if(this.mediaQuery.breakpointMaxWidth(this.mediaQuery.BREAKPOINTS.SM)) {
							if(outerIndex % (Math.floor(duration.asYears()) * 4) === 0) {
								nodes[index].style.visibility = 'visible';
							}
						}
						else {
							if(outerIndex % (Math.floor(duration.asYears()) * 2) === 0) {
								nodes[index].style.visibility = 'visible';
							}
						}
						outerIndex++;
					}
				}
			});
		})();
	}

	drawYAxes() {
		let data = this.getYAxisLeftData();
		let yMinTickValue = _.minBy(data, (datum) => {
			return datum;
		});

		let yMaxTickValue = _.maxBy(data, (datum) => {
			return datum;
		});
		
		this.svg.append('g')
			.attr('class', 'y-axis')
			.attr('transform', `translate(-5, 0)`)
			.call(d3.axisLeft(this.yAxisLeft).tickFormat((value, index) => {
				if(data.length > 1 && yMinTickValue !== yMaxTickValue) {
					// If mobile devices and the modulo by 4 is 1
					if(this.mediaQuery.breakpointMaxWidth(this.mediaQuery.BREAKPOINTS.SM)) {
						if(index % 4 === 1) {
							return value%1==0 ? value : value.toFixed(2);
						}
					}
					else {
						if(index % 3 === 1) {
							return value%1==0 ? value : value.toFixed(2);
						}
					}
					return '';
				}
				else {
					return value%1==0 ? value : value.toFixed(2);
				}
			}).tickSize(-20).tickPadding(15).tickValues(d3.range(this.yMinTickValue, this.yMaxTickValue, this.yTickValue)));

		this.getChartNode().select('.y-axis').selectAll('line').attr('x2', (value, index) => {
			if(data.length > 1 && yMinTickValue !== yMaxTickValue) {
				if(this.mediaQuery.breakpointMaxWidth(this.mediaQuery.BREAKPOINTS.SM)) {
					if(index === 0) {
						return 0;
					}
					else if(index % 4 === 1) {
						return -20;
					}
				}
				else {
					if(index === 0) {
						return 0;
					}
					else if(index % 3 === 1) {
						return -20;
					}
				}
			}
			return -10;
		});
	}

	drawPaths() {
		_.forEach(this.data, (datum) => {
			this.svg.append('path')
				.datum(datum.values)
				.attr('class', 'plot-line')
				.attr('d', d3.line()
					.x((innerDatum) => {
						return this.xAxisBottom(this.getMomentDate(innerDatum.date).toDate());
					})
					.y((innerDatum) => {
						return this.yAxisLeft(innerDatum.value);
					})
				);
		});
	}

	drawPoints() {
		_.forEach(this.data, (datum) => {
			this.svg.append('g')
				.selectAll('dot')
				.data(datum.values)
				.enter()
				.append('circle')
				.attr('class', 'plot-circle')
				.attr('cx', (innerDatum) => {
					return this.xAxisBottom(this.getMomentDate(innerDatum.date).toDate());
				})
				.attr('cy', (innerDatum) => {
					return this.yAxisLeft(innerDatum.value);
				})
				.attr('r', this.dotRadius)
				.on('mouseover', (innerDatum, innerIndex, nodes) => {
					this.tooltip.style('display', 'block');

					// Have the dots grow
					let circle = d3.select(nodes[innerIndex]);

					circle.attr('r', this.dotFocusRadius)
						.attr('cx', () => {
							return this.xAxisBottom(this.getMomentDate(innerDatum.date).toDate());
						})
						.attr('cy', () => {
							return this.yAxisLeft(innerDatum.value);
						});

					// Add active class to the related tick
					d3.select(nodes[innerIndex].parentNode.parentNode)
						.selectAll('.tick')
						.filter((innerInnerDatum, innerInnerIndex) => {
							return innerDatum.date === innerInnerDatum && innerIndex === innerInnerIndex;
						}).select('line').classed('active', true);
				})
				.on('mousemove', (innerDatum, innerIndex, nodes) => {
					this.tooltip.html(`
						<div class="${CLS}-chart-tooltip-micron">${innerDatum.valueLabel}</div>
						${(() => {
							if(!_.isNil(innerDatum.diffPercentLabel)) {
								let difference = !_.isNil(innerDatum.diff) && innerDatum.diff !== 0 ? innerDatum.diff : null;
								let cssClass = '';

								if(difference > 0) {
									cssClass = `${CLS}-chart-tooltip-difference--up`;
								}
								else if(difference < 0) {
									cssClass = `${CLS}-chart-tooltip-difference--down`;
								}

								return `<div class="${CLS}-chart-tooltip-difference ${cssClass}">${innerDatum.diffPercentLabel}</div>`;
							}
							return `<div class="${CLS}-chart-tooltip-difference">-</div>`;
						})()}
					`);
					let tooltipWidth = this.$tooltip.outerWidth();
					let tooltipHeight = this.$tooltip.outerHeight();
					let topPositionOffset = 30;

					// Subtract the left margin alongside half of the tooltip width. This horizontally centers the tooltip
					let leftPosition = (d3.mouse(nodes[innerIndex])[0] + this.margin.left) - (tooltipWidth / 2);
					let topPosition = ((d3.mouse(nodes[innerIndex])[1]) + this.margin.top) - tooltipHeight - topPositionOffset;

					this.tooltip.style('left', `${leftPosition}px`)
						.style('top', `${topPosition}px`);
				})
				.on('mouseleave', (innerDatum, innerIndex, nodes) => {
					this.tooltip.style('display', 'none');

					// Revert the dot size
					let circle = d3.select(nodes[innerIndex]);
					circle.attr('r', this.dotRadius)
						.attr('cx', () => {
							return this.xAxisBottom(this.getMomentDate(innerDatum.date).toDate());
						})
						.attr('cy', () => {
							return this.yAxisLeft(innerDatum.value);
						});

					// Revert active class to the related tick
					d3.select(nodes[innerIndex].parentNode.parentNode)
						.selectAll('.tick')
						.filter((innerInnerDatum, innerInnerIndex) => {
							return innerIndex === innerInnerIndex;
						}).select('line').classed('active', false);
				});
		});
	}

	drawAreaAesthetic() {
		let xMinTickDate = moment(this.xMinTickValue, 'DD/MM/YYYY');
		let xMaxTickDate = moment(this.xMaxTickValue, 'DD/MM/YYYY');
		let duration = moment.duration(xMaxTickDate.diff(xMinTickDate));

		let data = [];
		for(let i = 0; i <= Math.ceil(duration.asDays()); i++) {
			let date = moment(moment(xMinTickDate).add(i, 'days').format('DD/MM/YYYY'), 'DD/MM/YYYY');
			data.push(date);
		}

		_.forEach(this.data, (datum) => {
			if(datum.values.length > 1) {
				this.svg.append('path')
					.datum(data)
					.attr('class', 'areaAesthetic')
					.attr('d', d3.area()
						.x((innerDatum) => {
							return this.xAxisBottom(this.getMomentDate(innerDatum).toDate());
						})
						.y0(this.height)
						.y1((innerDatum, innerIndex, nodes) => {
							let firstNodeValue = datum.values[0].value;
							let lastNodeValue = datum.values[datum.values.length - 1].value;
							let dy;

							if(firstNodeValue <= lastNodeValue) {
								dy = Math.abs(lastNodeValue - firstNodeValue);
							}
							else {
								dy = Math.abs(firstNodeValue - lastNodeValue) * -1;
							}

							let dx = nodes.length - 1;
							let b = dy / dx;
							let y = firstNodeValue + (b * innerIndex);

							return this.yAxisLeft(y);
						})
					);
			}
		});
	}
}
